import { useEffect, useRef } from 'react'
import { VIDEO } from '../data/dgpt'
import { trackEvent, type AnalyticsEvent } from '../utils/analytics'

type VimeoPlayer = {
  on: (event: string, callback: (data: { percent?: number }) => void) => void
  off: (event: string) => void
}

type VimeoNamespace = {
  Player: new (element: HTMLIFrameElement) => VimeoPlayer
}

declare global {
  interface Window {
    Vimeo?: VimeoNamespace
  }
}

const MILESTONES: Array<[number, AnalyticsEvent]> = [
  [0.25, 'video_progress_25'],
  [0.5, 'video_progress_50'],
  [0.75, 'video_progress_75'],
  [1, 'video_progress_100'],
]

let apiPromise: Promise<VimeoNamespace> | null = null

/** Loads the Vimeo player API once — only called after the visitor presses Play. */
function loadVimeoApi(): Promise<VimeoNamespace> {
  if (window.Vimeo) return Promise.resolve(window.Vimeo)
  if (apiPromise) return apiPromise
  apiPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = VIDEO.playerApi
    script.async = true
    script.onload = () => (window.Vimeo ? resolve(window.Vimeo) : reject(new Error('Vimeo API unavailable')))
    script.onerror = () => {
      apiPromise = null
      reject(new Error('Vimeo API failed to load'))
    }
    document.head.appendChild(script)
  })
  return apiPromise
}

/** Fires 25/50/75/100% completion events for the active Vimeo iframe. */
export function useVimeoProgress(iframe: HTMLIFrameElement | null) {
  const fired = useRef(new Set<AnalyticsEvent>())

  useEffect(() => {
    if (!iframe) return
    let player: VimeoPlayer | null = null
    let cancelled = false

    const fire = (event: AnalyticsEvent) => {
      if (fired.current.has(event)) return
      fired.current.add(event)
      trackEvent(event)
    }

    loadVimeoApi()
      .then((Vimeo) => {
        if (cancelled) return
        player = new Vimeo.Player(iframe)
        player.on('timeupdate', ({ percent = 0 }) => {
          MILESTONES.forEach(([threshold, event]) => {
            if (percent >= threshold - 0.01) fire(event)
          })
        })
        player.on('ended', () => fire('video_progress_100'))
      })
      .catch(() => {
        /* Analytics only — playback is unaffected. */
      })

    return () => {
      cancelled = true
      player?.off('timeupdate')
      player?.off('ended')
    }
  }, [iframe])
}
